/*  Colega Link · Admin · Gestión de Membresías
    Ruta: /app/admin/planes
    - Lista pagos de planes en estado pending
    - Botón "Confirmar y activar plan" → POST /admin/plan-payments/{id}/mark-paid
    - Botón "Rechazar" → POST /admin/plan-payments/{id}/cancel (con motivo)
    - Muestra recibos y notifica por email vía Resend (backend)
*/
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import {
  Crown, RefreshCw, Loader2, Check, XCircle, User, Mail, Calendar,
  Coins, Copy, Sparkles, ExternalLink, ShieldAlert, Paperclip,
} from "lucide-react";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

const PLAN_LABEL = {
  pionero: "Pionero",
  embajador_pionero: "Pionero Fundador",
  premium: "Premium",
};
const CYCLE_LABEL = {
  monthly: "Mensual",
  annual: "Anual",
  null: "Cuota única",
};

export default function AdminPlanPayments() {
  const [tab, setTab] = useState("pending"); // pending | paid | canceled
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");   // payment_id being processed
  const [flash, setFlash] = useState({ type: "", text: "" });
  const [reject, setReject] = useState(null); // { payment } | null

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/plan-payments?status=${tab}`);
      setItems(r.data?.payments || []);
    } catch (err) {
      setFlash({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab]);// eslint-disable-line react-hooks/exhaustive-deps

  const flashTimeout = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash({ type: "", text: "" }), 6000);
  };

  const approve = async (payment) => {
    if (!window.confirm(`Confirmar pago de ${CLP(payment.amount_clp)} de ${payment.user_name || payment.user_email}?\n\nEsto activará el plan ${PLAN_LABEL[payment.plan_target]} y enviará un email de bienvenida.`)) return;
    setBusy(payment.id);
    try {
      const r = await api.post(`/admin/plan-payments/${payment.id}/mark-paid`, {});
      flashTimeout({
        type: "success",
        text: `✓ Plan ${PLAN_LABEL[payment.plan_target]} activado para ${payment.user_email}. Email: ${r.data?.email_sent ? "enviado" : "no enviado (verifica Resend)"}.`,
      });
      await load();
    } catch (err) {
      flashTimeout({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setBusy("");
    }
  };

  const submitReject = async (reason) => {
    const payment = reject.payment;
    setBusy(payment.id);
    try {
      const r = await api.post(`/admin/plan-payments/${payment.id}/cancel`, { reason });
      flashTimeout({
        type: "success",
        text: `Pago rechazado. Email al abogado: ${r.data?.email_sent ? "enviado" : "no enviado"}.`,
      });
      setReject(null);
      await load();
    } catch (err) {
      flashTimeout({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setBusy("");
    }
  };

  const totalPending = useMemo(() => items
    .filter((p) => p.status === "pending")
    .reduce((s, p) => s + (p.amount_clp || 0), 0),
    [items]);

  return (
    <div className="container-app py-10 max-w-6xl" data-testid="admin-plan-payments">
      <AdminNav />

      <header className="flex items-center gap-3 mb-6">
        <Crown size={26} className="text-amber-400" />
        <div>
          <h1 className="serif text-3xl leading-tight">Membresías · Pagos de Planes</h1>
          <p className="text-sm text-muted-foreground">
            Confirma o rechaza los pagos de suscripción de abogados. Al aprobar, se activa el plan y se envía el email de bienvenida.
          </p>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-white/5">
        {[
          { k: "pending", label: "Pendientes", tone: "amber" },
          { k: "paid", label: "Confirmados", tone: "emerald" },
          { k: "canceled", label: "Rechazados", tone: "red" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.k
                ? `border-${t.tone}-400 text-${t.tone}-200`
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
          data-testid="refresh-btn"
          disabled={loading}
        >
          {loading ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>} Actualizar
        </button>
      </div>

      {flash.text && (
        <div
          className={`mb-4 p-3 rounded-md text-sm ${
            flash.type === "success"
              ? "bg-emerald-400/10 border border-emerald-400/30 text-emerald-200"
              : "bg-red-400/10 border border-red-400/30 text-red-200"
          }`}
          data-testid="flash"
        >
          {flash.text}
        </div>
      )}

      {tab === "pending" && items.length > 0 && (
        <div className="mb-4 p-3 rounded-md border border-amber-400/25 bg-amber-400/5 text-sm flex items-center justify-between">
          <span><ShieldAlert size={14} className="inline mr-1 text-amber-300"/> {items.length} pago{items.length === 1 ? "" : "s"} pendiente{items.length === 1 ? "" : "s"} de confirmación</span>
          <span className="text-amber-300 font-semibold">Total: {CLP(totalPending)}</span>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-muted-foreground text-sm">Cargando pagos…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center panel">
          <Sparkles size={22} className="text-muted-foreground/50 mx-auto"/>
          <div className="mt-3 text-sm text-muted-foreground">
            {tab === "pending" ? "No hay pagos pendientes por confirmar." :
             tab === "paid" ? "Aún no hay pagos confirmados." :
             "No hay pagos rechazados."}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <PaymentCard
              key={p.id}
              payment={p}
              onApprove={() => approve(p)}
              onReject={() => setReject({ payment: p })}
              busy={busy === p.id}
              tab={tab}
            />
          ))}
        </div>
      )}

      {reject && (
        <RejectModal
          payment={reject.payment}
          onClose={() => setReject(null)}
          onSubmit={submitReject}
          busy={busy === reject.payment.id}
        />
      )}
    </div>
  );
}

function PaymentCard({ payment, onApprove, onReject, busy, tab }) {
  const [copiedRef, setCopiedRef] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const copy = (t) => {
    navigator.clipboard.writeText(t);
    setCopiedRef(true);
    setTimeout(() => setCopiedRef(false), 1500);
  };
  const created = payment.created_at ? new Date(payment.created_at).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
  const paidAt = payment.paid_at ? new Date(payment.paid_at).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : null;
  return (
    <div className="panel p-4 sm:p-5" data-testid={`payment-${payment.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        {/* Info principal */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${
              payment.plan_target === "pionero" ? "bg-amber-400/15 text-amber-300 border border-amber-400/30" :
              payment.plan_target === "embajador_pionero" ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30" :
              "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30"
            }`}>
              <Crown size={10}/> {PLAN_LABEL[payment.plan_target] || payment.plan_target}
            </span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
              {CYCLE_LABEL[payment.billing_cycle] || "Cuota única"}
            </span>
            {payment.pionero_credit_applied > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/25 text-amber-200 font-semibold">
                Abono Pionero: -{CLP(payment.pionero_credit_applied)}
              </span>
            )}
          </div>

          <div className="mt-2 flex items-center gap-2">
            <User size={14} className="text-muted-foreground"/>
            <span className="font-medium">{payment.user_name || "—"}</span>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
            <Mail size={12}/> <a href={`mailto:${payment.user_email}`} className="hover:text-foreground underline decoration-dotted">{payment.user_email}</a>
          </div>

          <div className="mt-3 flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Calendar size={11}/> Registrado: {created}</span>
            {paidAt && <span className="inline-flex items-center gap-1 text-emerald-300"><Check size={11}/> Confirmado: {paidAt}</span>}
            {payment.reference && (
              <button
                onClick={() => copy(payment.reference)}
                className="inline-flex items-center gap-1 hover:text-foreground"
                data-testid={`copy-ref-${payment.id}`}
              >
                <Copy size={11}/> Ref: <span className="font-mono">{payment.reference}</span>
                {copiedRef && <span className="text-emerald-300 ml-1">✓</span>}
              </button>
            )}
            {payment.receipt_uploaded_at && (
              <button
                onClick={() => setReceiptOpen(true)}
                className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 underline decoration-dotted"
                data-testid={`view-receipt-${payment.id}`}
              >
                <Paperclip size={11}/> Comprobante subido · ver
              </button>
            )}
          </div>

          {payment.reason && tab !== "pending" && (
            <div className={`mt-3 p-2 rounded-md text-xs ${
              tab === "canceled" ? "bg-red-400/5 border border-red-400/20 text-red-200" : "bg-white/5 border border-white/10 text-muted-foreground"
            }`}>
              <strong>Motivo:</strong> {payment.reason}
            </div>
          )}
        </div>

        {/* Monto + acciones */}
        <div className="sm:text-right shrink-0">
          <div className="serif text-3xl text-amber-200" data-testid={`amount-${payment.id}`}>{CLP(payment.amount_clp)}</div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Total a recibir</div>

          {tab === "pending" ? (
            <div className="mt-4 flex gap-2 sm:justify-end flex-wrap">
              <button
                onClick={onReject}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-red-400/40 text-red-200 hover:bg-red-400/10 disabled:opacity-40"
                data-testid={`reject-${payment.id}`}
              >
                <XCircle size={13}/> Rechazar
              </button>
              <button
                onClick={onApprove}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 disabled:opacity-40"
                data-testid={`approve-${payment.id}`}
              >
                {busy ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>}
                Confirmar y activar plan
              </button>
            </div>
          ) : (
            <a
              href={`/app/admin/profesionales?openUserId=${payment.user_id}`}
              className="mt-4 inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200 sm:justify-end"
              data-testid={`view-user-${payment.id}`}
            >
              Ver ficha del abogado <ExternalLink size={11}/>
            </a>
          )}
        </div>
      </div>
      {receiptOpen && <ReceiptViewer paymentId={payment.id} onClose={() => setReceiptOpen(false)}/>}
    </div>
  );
}

function ReceiptViewer({ paymentId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/admin/plan-payments/${paymentId}/receipt`)
      .then((r) => setDoc(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [paymentId]);
  const isPdf = doc?.receipt_mime === "application/pdf";
  const src = doc ? `data:${doc.receipt_mime};base64,${doc.receipt_b64}` : null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="receipt-viewer">
      <div className="panel max-w-3xl w-full p-4 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-sm">
            <div className="label-eyebrow text-cyan-300">Comprobante subido por el usuario</div>
            {doc?.receipt_name && <div className="mt-0.5 text-xs text-muted-foreground">{doc.receipt_name} · {doc.receipt_uploaded_at?.slice(0,16).replace("T"," ")}</div>}
          </div>
          <div className="flex items-center gap-2">
            {src && <a href={src} download={doc?.receipt_name || "comprobante"} className="text-xs px-3 py-1.5 rounded-md border border-white/15 hover:bg-white/5" data-testid="receipt-download">Descargar</a>}
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="receipt-close">✕</button>
          </div>
        </div>
        {err && <div className="text-sm text-red-300">{err}</div>}
        {!doc && !err && <div className="text-sm text-muted-foreground">Cargando…</div>}
        {src && (
          isPdf
            ? <iframe title="comprobante" src={src} className="w-full h-[70vh] bg-white rounded-md"/>
            : <img src={src} alt="Comprobante" className="w-full max-h-[75vh] object-contain rounded-md bg-black/40"/>
        )}
      </div>
    </div>
  );
}

function RejectModal({ payment, onClose, onSubmit, busy }) {
  const [reason, setReason] = useState("");
  const submit = () => {
    if (!reason.trim()) return;
    onSubmit(reason.trim());
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="reject-modal">
      <div className="panel max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <XCircle size={22} className="text-red-300 shrink-0 mt-1"/>
          <div className="flex-1">
            <h3 className="serif text-xl">Rechazar pago</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {payment.user_name || payment.user_email} · {PLAN_LABEL[payment.plan_target]} · {CLP(payment.amount_clp)}
            </p>
          </div>
        </div>
        <div className="mt-5">
          <label className="text-xs label-eyebrow">Motivo del rechazo (obligatorio)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={300}
            placeholder="Ej: No encontramos la transferencia por $15.000 CLP en la cuenta corriente durante los últimos 3 días."
            className="field mt-1.5 w-full text-sm"
            data-testid="reject-reason"
            required
          />
          <p className="text-[10px] text-muted-foreground mt-1">Se enviará un email al abogado con este motivo.</p>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground" data-testid="reject-cancel">Cancelar</button>
          <button
            onClick={submit}
            disabled={!reason.trim() || busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-red-500 hover:bg-red-400 text-red-950 disabled:opacity-40"
            data-testid="reject-confirm"
          >
            {busy ? <Loader2 size={13} className="animate-spin"/> : <Coins size={13}/>}
            Rechazar y notificar
          </button>
        </div>
      </div>
    </div>
  );
}
